import React, { useEffect, useRef, useState, useCallback } from "react";
import { Send } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";

export const Chat = ({ channel, height = 280 }) => {
  const { t } = useLang();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const scrollRef = useRef(null);
  const countRef = useRef(0);

  const load = useCallback(async () => {
    if (!channel) return;
    try {
      const { data } = await api.get(`/chat/${channel}/messages`);
      setMessages(data);
    } catch (e) { console.warn("chat load failed:", e); }
  }, [channel]);

  useEffect(() => {
    load();
    const iv = setInterval(load, 2000);
    return () => clearInterval(iv);
  }, [load]);

  useEffect(() => {
    if (messages.length !== countRef.current && scrollRef.current) {
      countRef.current = messages.length;
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendText = async (value) => {
    const v = (value || "").trim();
    if (!v) return;
    try {
      const { data } = await api.post(`/chat/${channel}/messages`, { text: v });
      setMessages((m) => [...m, data]);
    } catch (e) { console.warn("chat send failed:", e); setText(v); }
  };

  const send = async (e) => {
    e.preventDefault();
    const value = text.trim();
    if (!value) return;
    setText("");
    sendText(value);
  };

  const EMOJIS = ["😎", "🔥", "😂", "💸", "📈", "📉", "🤝", "🎯", "😱", "🫡"];

  return (
    <div className="flex flex-col border border-white/10 bg-panel" data-testid="chat-panel">
      <div className="px-3 py-2 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-white/40">
        {t("chat_title")}
      </div>
      <div ref={scrollRef} style={{ height }} className="overflow-y-auto px-3 py-2 space-y-2">
        {messages.length === 0 ? (
          <div className="text-white/30 font-mono text-xs py-4">{t("no_messages")}</div>
        ) : (
          messages.map((m) => (
            <div key={m.id} className="text-sm">
              <span className="font-mono text-xs text-signal mr-2">{m.name}</span>
              <span className="text-white/80 break-words">{m.text}</span>
            </div>
          ))
        )}
      </div>
      <div className="flex gap-1 px-2 py-1.5 border-t border-white/10 overflow-x-auto" data-testid="emoji-bar">
        {EMOJIS.map((em) => (
          <button
            key={em}
            type="button"
            data-testid={`emoji-${em}`}
            onClick={() => sendText(em)}
            className="text-base leading-none px-1.5 py-1 rounded hover:bg-white/10 transition-colors shrink-0"
          >
            {em}
          </button>
        ))}
      </div>
      <form onSubmit={send} className="flex border-t border-white/10">
        <input
          data-testid="chat-input"
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={t("chat_placeholder")}
          maxLength={400}
          className="flex-1 bg-transparent px-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none"
        />
        <button
          data-testid="chat-send"
          type="submit"
          className="px-4 text-signal hover:bg-signal/10 transition-colors border-l border-white/10"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );
};
